import { useRef, useState, useImperativeHandle, forwardRef, useMemo } from 'react'
import { ReactFlow, Background, useReactFlow, MarkerType, type Edge, type Node } from '@xyflow/react'
import { useTranslation } from 'react-i18next'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { type TChatbotNode } from '../node.type'
import { ChatbotPreviewNode } from './chatbot-preview-node.component'
import { ChatbotPreviewControls } from './chatbot-preview-controls.component'
import {
  exportChatbotPdfPreview,
  exportChatbotPdfGrid,
  type TPdfOrientation,
} from './chatbot-pdf-export.helper'

const previewNodeTypes = {
  chatbotMessage: ChatbotPreviewNode,
} as const

// Green → yellow → orange → red progression for range handles
const RANGE_EDGE_COLORS = ['#16a34a', '#84cc16', '#eab308', '#f97316', '#dc2626', '#991b1b']

function getEdgeColor(sourceHandle: string | null | undefined): string {
  if (sourceHandle === 'yes') return '#16a34a'
  if (sourceHandle === 'no') return '#dc2626'
  if (sourceHandle?.startsWith('range-')) {
    const index = parseInt(sourceHandle.slice('range-'.length), 10)
    return RANGE_EDGE_COLORS[index % RANGE_EDGE_COLORS.length] ?? '#6b7280'
  }
  return '#6b7280'
}

export type TChatbotPreviewFlowProps = {
  previewNodes: TChatbotNode[]
  edges: Edge[]
  chatbotName: string
  followupName: string
  autoLayout: boolean
}

export type TChatbotPreviewFlowHandle = {
  exportPdfPreview: (params: { zoomOverride?: number; orientation: TPdfOrientation }) => Promise<void>
  exportPdfGrid: (params: { orientation: TPdfOrientation }) => Promise<void>
}

export const ChatbotPreviewFlow = forwardRef<TChatbotPreviewFlowHandle, TChatbotPreviewFlowProps>(
  function ChatbotPreviewFlow(
    { previewNodes, edges, chatbotName, followupName, autoLayout }: Readonly<TChatbotPreviewFlowProps>,
    ref,
  ) {
    const { t } = useTranslation()
    const { project } = useCurrentProject()
    const { getNodes, getEdges } = useReactFlow()
    const flowContainerRef = useRef<HTMLDivElement>(null)
    const [overrideNodes, setOverrideNodes] = useState<Node[] | null>(null)
    const [overrideEdges, setOverrideEdges] = useState<Edge[] | null>(null)

    const edgesWithArrows = useMemo(
      () =>
        (overrideEdges ?? edges).map((edge) => {
          const color = getEdgeColor(edge.sourceHandle)
          return {
            ...edge,
            style: { stroke: color, strokeWidth: 2 },
            markerEnd: { type: MarkerType.ArrowClosed, color },
          }
        }),
      [overrideEdges, edges],
    )

    const applyLayout = async (gridNodes: Node[], gridEdges: Edge[]) => {
      setOverrideNodes(gridNodes)
      setOverrideEdges(gridEdges)
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setTimeout(resolve, 100)
          })
        })
      })
    }

    const restoreLayout = () => {
      setOverrideNodes(null)
      setOverrideEdges(null)
    }

    const pdfLabels = {
      chatbotLabel: t('pages.configuration.followups.chatbot.preview.pdf-chatbot-label'),
      establishmentLabel: t('pages.configuration.followups.chatbot.preview.pdf-establishment'),
      interventionLabel: t('pages.configuration.followups.chatbot.preview.pdf-intervention'),
      exportDateLabel: t('pages.configuration.followups.chatbot.preview.pdf-export-date'),
      legendLabel: t('pages.configuration.followups.chatbot.preview.pdf-legend'),
      alertResponseLabel: t('pages.configuration.followups.chatbot.preview.pdf-alert-response'),
      normalResponseLabel: t('pages.configuration.followups.chatbot.preview.pdf-normal-response'),
      contentLabel: t('pages.configuration.followups.chatbot.preview.pdf-content'),
      pageLabel: (current: number, total: number) =>
        t('pages.configuration.followups.chatbot.preview.pdf-page', { current, total }),
    }

    useImperativeHandle(ref, () => ({
      exportPdfPreview: async ({ zoomOverride, orientation }) => {
        if (!flowContainerRef.current) return
        await exportChatbotPdfPreview({
          flowContainer: flowContainerRef.current,
          nodes: getNodes(),
          edges: getEdges(),
          chatbotName,
          projectName: project.name,
          followupName,
          labels: pdfLabels,
          applyLayout,
          restoreLayout,
          zoomOverride,
          orientation,
          autoLayout,
        })
      },
      exportPdfGrid: async ({ orientation }) => {
        if (!flowContainerRef.current) return
        await exportChatbotPdfGrid({
          flowContainer: flowContainerRef.current,
          nodes: getNodes(),
          edges: getEdges(),
          chatbotName,
          projectName: project.name,
          followupName,
          labels: pdfLabels,
          applyLayout,
          restoreLayout,
          orientation,
        })
      },
    }))

    return (
      <div
        ref={flowContainerRef}
        className="c-h-[calc(95vh-220px)] c-border c-border-gray-200 c-rounded-md c-bg-gray-50 c-relative"
      >
        <ReactFlow
          nodes={overrideNodes ?? previewNodes}
          edges={edgesWithArrows}
          nodeTypes={previewNodeTypes}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          fitView
          minZoom={0.1}
          maxZoom={1.5}
        >
          <Background />
          <ChatbotPreviewControls />
        </ReactFlow>
      </div>
    )
  },
)
